import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { BookOpen, Users, TrendingDown, Scale, Brain, DollarSign, Building2, Heart, ChevronRight } from 'lucide-react';

const researchArticles = [
  {
    id: 1,
    title: 'Demographics in Borrowing and Lending: Who Gets Left Behind?',
    category: 'Market Analysis',
    icon: Users,
    color: 'from-blue-500/20 to-cyan-500/20',
    border: 'border-blue-500/20',
    content: `The borrowing and lending landscape in the United States has long been shaped by demographic factors that create invisible barriers for millions of Americans. Traditional credit scoring models disproportionately impact communities of color, younger borrowers, and immigrants who may lack conventional credit histories despite being financially responsible.

Studies consistently show that zip code, age, and ethnic background remain predictive factors in loan approval rates and interest rate determination, not because of actual creditworthiness, but because of systemic biases baked into legacy scoring models. Low-income neighborhoods often face "lending deserts" where access to fair-rate products is virtually nonexistent.

KOJIRU's AI-driven approach is designed to evaluate borrowers on verifiable on-chain behavior, staking commitment, and real-time repayment patterns rather than demographic factors. By moving credit assessment to the blockchain, we aim to create a more transparent, auditable system where borrowers are evaluated on financial merit. AI models have not been independently audited for fair lending compliance; Kojiru is committed to ongoing testing for disparate impact.`
  },
  {
    id: 2,
    title: 'Benefits and Pitfalls of Lending: Impact on the Middle Class',
    category: 'Economic Research',
    icon: Scale,
    color: 'from-purple-500/20 to-pink-500/20',
    border: 'border-purple-500/20',
    content: `For America's middle class, lending is both a lifeline and a potential trap. Home mortgages, auto loans, student debt, and small business credit are the engines of middle-class wealth creation. Yet the traditional lending system extracts enormous value through origination fees, variable interest rates, and predatory terms that can turn a wealth-building tool into a debt spiral.

The average American household carries over $100,000 in debt, with a significant portion paying interest rates far above what their actual risk profile warrants. Credit card debt alone costs middle-class families thousands per year in interest: money that could be building wealth, funding education, or supporting retirement.

The pitfalls are clear: opaque terms, hidden fees, and a system designed to maximize lender profit rather than borrower outcomes. But the benefits of accessible credit are equally undeniable: without it, home ownership, higher education, and entrepreneurship become impossible dreams for most families.

This tension is precisely what KOJIRU was built to resolve. By eliminating intermediary costs, automating underwriting through AI, and leveraging DeFi's transparent smart contracts, we preserve all the benefits of credit access while stripping away the exploitative elements that have burdened the middle class for generations.`
  },
  {
    id: 3,
    title: 'Making the Loan Process More Suitable and Favorable',
    category: 'Policy Research',
    icon: TrendingDown,
    color: 'from-green-500/20 to-teal-500/20',
    border: 'border-green-500/20',
    content: `The modern loan application process is a relic of a pre-digital era: paper-heavy, time-consuming, and deliberately opaque. Borrowers routinely spend weeks waiting for approvals, face last-minute rate changes, and navigate a maze of fine print designed to benefit lenders at the expense of clarity.

What's required to fix this? Three fundamental shifts:

First, radical transparency. Every fee, rate calculation, and term condition should be visible, immutable, and verifiable before a borrower commits. Smart contracts on blockchain deliver this by encoding loan terms in auditable code that executes exactly as written.

Second, speed and accessibility. AI-powered underwriting can assess creditworthiness in seconds rather than weeks, using richer data sets that capture true financial behavior rather than relying on a single three-digit credit score.

Third, competitive pricing. When lenders compete directly for borrowers in an open marketplace, rather than through oligopolistic banks, rates naturally fall to fair market levels. KOJIRU's peer-to-peer model creates exactly this dynamic: lenders bid against each other, and borrowers receive the best available terms.

The result is a loan process that is faster, fairer, and fundamentally more favorable to the people who need credit most.`
  },
  {
    id: 4,
    title: 'How Lenders Can Lower Their Cost of Acquiring New Borrowers',
    category: 'Lender Strategy',
    icon: DollarSign,
    color: 'from-orange-500/20 to-yellow-500/20',
    border: 'border-orange-500/20',
    content: `Customer acquisition cost (CAC) is the silent killer of lending profitability. Traditional lenders spend between $300 and $2,000 to acquire a single borrower through advertising, branch operations, broker commissions, and sales staff. These costs are inevitably passed on to borrowers through higher interest rates and fees.

KOJIRU fundamentally restructures the economics of borrower acquisition for lenders:

Platform-level matching eliminates marketing spend. Lenders on KOJIRU don't advertise: they browse verified loan requests from borrowers who have already committed to the platform by staking KOJIRU tokens. The borrower's stake signals genuine intent, dramatically reducing time wasted on unqualified leads.

AI-assisted bid optimization means lenders make smarter decisions faster. Instead of employing teams of analysts to evaluate each opportunity, KOJIRU's AI provides instant risk assessments and suggested APR rates, allowing lenders to deploy capital efficiently at scale.

Smart contract automation removes operational overhead. Loan origination, disbursement, repayment collection, and default management are all handled by immutable smart contracts: no loan officers, no collections departments, no administrative bloat.

The net effect: lender CAC on KOJIRU approaches near zero, allowing them to offer genuinely competitive rates while maintaining healthy margins. Everyone wins.`
  },
  {
    id: 5,
    title: 'KOJIRU: Leveling the Playing Field for All Borrowers',
    category: 'Platform Vision',
    icon: Heart,
    color: 'from-red-500/20 to-pink-500/20',
    border: 'border-red-500/20',
    content: `The traditional financial system was not designed with equality in mind. It was built to serve those who already have wealth, credit history, and access to institutional relationships. For the working family trying to consolidate debt, the recent graduate seeking a small business loan, or the immigrant entrepreneur with no U.S. credit history: the system is stacked against them.

KOJIRU exists to change this equation. Here's how we level the playing field:

Lower fees: By eliminating bank branches, loan officers, and layers of intermediaries, KOJIRU reduces origination fees to a fraction of traditional costs. These savings go directly into borrowers' pockets.

Fair interest rates: Our AI credit analysis evaluates actual financial behavior, not proxies like zip code or employment history. Borrowers are assessed on what they do, not who they are. This means genuinely creditworthy borrowers who would be penalized by traditional models receive the rates they deserve.

Transparent terms: Every loan on KOJIRU is a smart contract, visible, immutable, and identical for every borrower. No hidden fees, no bait-and-switch rate adjustments, no fine print surprises.

The overall cost of borrowing through KOJIRU is significantly lower than traditional alternatives, which is transformative for middle-class Americans who have been squeezed by decades of rising costs and stagnant wages. When you reduce the cost of credit, you unlock home ownership, education, entrepreneurship, and wealth building for millions who were previously excluded.`
  },
  {
    id: 6,
    title: 'KOJIRU at a Pivotal Moment: DeFi Lending in the Age of Inflation',
    category: 'Economic Commentary',
    icon: Building2,
    color: 'from-indigo-500/20 to-blue-500/20',
    border: 'border-indigo-500/20',
    content: `America is at a crossroads. After years of unprecedented inflation, rising interest rates, and economic uncertainty, the middle class is under more financial pressure than at any point in recent memory. The cost of housing, education, healthcare, and everyday goods has outpaced wage growth, leaving families increasingly dependent on credit to bridge the gap.

In this environment, the traditional lending system has become part of the problem. Banks have raised rates aggressively, credit card APRs exceed 20% for many consumers, and student loan payments have resumed after years of forbearance. The people who need credit most are paying the highest prices for it.

This is precisely the moment KOJIRU was built for.

By leveraging decentralized finance on the Base blockchain, KOJIRU removes the institutional overhead that drives up lending costs. By using AI-powered credit analysis through a frontier LLM, we make faster, fairer decisions that don't penalize borrowers for macroeconomic conditions beyond their control. By creating a competitive peer-to-peer marketplace, we let market forces, not bank pricing committees, determine fair rates.

KOJIRU is determined to help all of America grow its wealth, even in challenging economic times. Lower borrowing costs mean more money for families to save, invest, and build. Fair access to credit means more small businesses, more homeowners, and a stronger middle class.

The convergence of AI and blockchain technology has created an opportunity to fundamentally reshape lending in America. KOJIRU is seizing that opportunity, not just as a business, but as a mission to make financial services work for everyone, not just the privileged few.`
  },
  {
    id: 7,
    title: 'The Future of AI in Borrowing and Lending',
    category: 'Technology',
    icon: Brain,
    color: 'from-violet-500/20 to-purple-500/20',
    border: 'border-violet-500/20',
    content: `Artificial intelligence is transforming every industry it touches, and lending is no exception. But the question isn't whether AI will change borrowing and lending: it's whether that change will benefit consumers or further entrench institutional advantages.

Today's AI in traditional banking is primarily used to maximize bank profits: optimizing pricing, predicting default to deny borderline applications, and targeting consumers with high-interest products they're statistically likely to accept. The borrower rarely sees the benefit.

KOJIRU takes the opposite approach. Our AI works for the borrower and the lender equally:

For borrowers, our Gemini-powered credit analysis provides personalized insights on how to improve their creditworthiness, access better rates, and build long-term financial health. It's not a gatekeeping tool: it's a coaching tool.

For lenders, our AI bid suggestion engine analyzes loan requests in real-time, assessing borrower risk, competitive dynamics, and market conditions to recommend optimal APR rates. This means better returns for lenders and fairer prices for borrowers: simultaneously.

The combination of AI and blockchain creates something neither technology could achieve alone: intelligent financial services that are also transparent and trustworthy. The AI makes smart decisions; the blockchain ensures those decisions are visible, auditable, and fair.

As we look ahead, KOJIRU envisions a future where AI agents autonomously manage lending portfolios, where credit decisions happen in milliseconds with full transparency, and where the cost of financial intermediation approaches zero. That future is closer than most people think, and KOJIRU is building it today.`
  }
];

export default function Research() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-purple-400 mb-6">
              <BookOpen className="w-4 h-4" />
              KOJIRU Research
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="research-heading">
              Research & <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Insights</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              In-depth analysis on how AI-powered DeFi lending can transform borrowing for the American middle class: lowering costs, increasing access, and building wealth for all.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-5xl space-y-6">
          {researchArticles.map((article, i) => (
            <motion.details
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              viewport={{ once: true }}
              data-testid={`research-article-${article.id}`}
              className={`group rounded-2xl border ${article.border} bg-white/[0.02] overflow-hidden`}
            >
              <summary className={`flex items-center gap-4 p-6 cursor-pointer select-none bg-gradient-to-r ${article.color} hover:brightness-110 transition-all`}>
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <article.icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">{article.category}</div>
                  <h3 className="text-lg font-semibold leading-tight">{article.title}</h3>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 pt-4">
                <div className="pl-16">
                  {article.content.split('\n\n').map((para, j) => (
                    <p key={j} className="text-sm text-gray-400 leading-relaxed mb-4 last:mb-0">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </motion.details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-10 rounded-2xl bg-gradient-to-br from-blue-500/10 via-[#0a0a0a] to-purple-500/10 border border-white/10"
          >
            <h3 className="text-2xl font-bold mb-4">Join the Lending Revolution</h3>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">
              KOJIRU is building a fairer financial future. Explore our platform, stake KOJIRU tokens, and be part of the change.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="/tokenomics" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 rounded-xl font-medium transition-colors">
                View Tokenomics
              </a>
              <a href="/auth" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-blue-500 rounded-xl font-medium transition-colors">
                Get Started
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
